import Image from 'next/image';
import clsx from 'clsx';
import Badge from '@/components/pages/badge';
import type { AttorneyWithHomeOfficePhone } from '@jlg-website/database/types';
import { ArrowRight, Mark } from '@/components/pages/icons';
import Link from 'next/link';

export function TeamCarouselItem({
  member,
  showLocation = false,
  showLink,
  className,
  ctaPositioning = 'bottom',
}: {
  member: AttorneyWithHomeOfficePhone;
  showLocation?: boolean;
  showLink?: boolean;
  className?: string;
  ctaPositioning?: 'top' | 'bottom';
}) {
  const isTopCta = ctaPositioning === 'top';

  const wrapperClassName = clsx(
    'relative rounded-2xl overflow-hidden shrink-0 cursor-pointer group block',
    className ?? 'w-75 h-95'
  );

  const content = (
    <>
      <Image
        src={member.picture || ''}
        alt={member.name}
        width={318}
        height={413}
        className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105 aspect-77/100"
        sizes="300px"
      />

      <div className="group-hover:opacity-0 absolute bottom-0 left-0 right-0 h-[141px] rounded-b-2xl bg-black/30 backdrop-blur-[11.5px] [-webkit-mask-image:linear-gradient(to_bottom,transparent_0%,rgba(0,0,0,0.46)_36.5%,black_63%)] mask-[linear-gradient(to_bottom,transparent_0%,rgba(0,0,0,0.46)_36.5%,black_63%)] transition-opacity duration-300" />

      {showLocation && (
        <div className="absolute top-3 left-3">
          <Badge icon={<Mark className="w-3.5 h-3.5" />} label={'Cleveland, OH'} />
        </div>
      )}

      {showLink && (
        <div
          className={clsx(
            'absolute z-10 shrink-0 rounded-full bg-brand-button group-hover:brightness-90 flex items-center justify-center text-white transition-all duration-200 shadow-lg',
            isTopCta
              ? 'top-4 right-4 @lg:top-auto @lg:bottom-4 w-8 h-8 @lg:w-12 @lg:h-12'
              : 'bottom-5 right-5 w-11 h-11'
          )}
        >
          <ArrowRight className={isTopCta ? 'w-4 h-4 @lg:w-6 @lg:h-6' : 'w-5 h-5'} />
        </div>
      )}

      <div
        className={clsx(
          'absolute bottom-0 left-0 right-0 z-20',
          isTopCta ? 'p-4' : 'p-5',
          showLink && (isTopCta ? '@lg:pr-16' : 'pr-16')
        )}
      >
        <h3
          className={clsx(
            'text-white font-serif leading-[1.2]',
            isTopCta
              ? 'font-medium text-2xl tracking-[-0.48px] @lg:text-[32px] @lg:tracking-[-0.64px]'
              : 'font-semibold text-[32px] tracking-[-0.64px]'
          )}
        >
          {member.name}
        </h3>
        <p
          className={clsx(
            'text-white',
            isTopCta
              ? 'text-sm font-medium leading-5 tracking-[0.21px] @lg:text-base @lg:font-normal @lg:leading-normal @lg:tracking-normal'
              : 'text-base font-bold'
          )}
        >
          {member.content.en.data.degree}
        </p>
      </div>
    </>
  );

  if (showLink) {
    return (
      <Link href={`/team/${member.slug}`} className={wrapperClassName}>
        {content}
      </Link>
    );
  }

  return <div className={wrapperClassName}>{content}</div>;
}

export default TeamCarouselItem;
